import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent';
import { PageHeader } from '../components/templates/PageHeader';
import { Section } from '../components/templates/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Button } from '../components/atoms/Button';
import { Card } from '../components/atoms/Card';
import { FieldError } from '../components/atoms/FieldError/FieldError';
import { Inline } from '../components/atoms/Inline';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { PhotoUploadDropzone } from '../components/atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { Stack } from '../components/atoms/Stack';
import { Text } from '../components/atoms/Typography';
import { AiLimitErrorMessage } from '../components/aiUsage/AiLimitNotice';
import { useTranslation } from '../i18n/useTranslation';
import { ZeovitModuleGate } from '../components/zeovit/ZeovitModuleGate';
import { ZeovitUnavailableNotice } from '../components/zeovit/ZeovitUnavailableNotice';
import { ZeovitInputsNotice } from '../components/zeovit/ZeovitInputsNotice';
import { ZeovitConcernsFieldset } from '../components/zeovit/ZeovitConcernsFieldset';
import { ZeovitVolumeRequiredNotice } from '../components/zeovit/ZeovitVolumeRequiredNotice';
import { AQUARIUM_VOLUME_REQUIRED_CODE, type ZeovitConcern } from '../constants/zeovitConcerns';
import { SUPPLY_SYSTEM_NOT_ZEOVIT } from '../utils/supplySystem';
import { useCreateZeovitRecommendation } from '../hooks/useZeovitRecommendations';
import { getAiLimitErrorKind } from '../services/aiUsageService';
import { uploadGalleryImages } from '../services/galleryService';
import { ApiError } from '../lib/apiClient';
import { useToast } from '../stores/toastStore';
import './ZeovitNewRecommendationPage.css';

const PAGE_HEADING_ID = 'zeovit-new-recommendation-title';
const MAX_PHOTO_MB = 10;

type PhotoError = 'required' | 'unavailable' | null;
/**
 * `generic`: show the retryable error alert; `aiLimit`: the AI budget/availability notice;
 * `notZeovit`: the aquarium's supply system is not ZeoVit (409 SUPPLY_SYSTEM_NOT_ZEOVIT).
 */
type SubmitError =
  | { kind: 'generic' }
  | { kind: 'aiLimit'; error: unknown }
  | { kind: 'notZeovit' }
  // 409 AQUARIUM_VOLUME_REQUIRED: doses are calculated per litre (v3).
  | { kind: 'volume' }
  | null;

/** The uploaded reference photo, kept so a retry does not upload the same file again. */
interface UploadedPhoto {
  file: File;
  key: string;
}

/**
 * New ZeoVit dosing recommendation (#1444): reference photo → POST
 * /api/v1/aquariums/:aquariumId/zeovit-recommendations, then the Recommendation Detail
 * page of the generated recommendation. Measurements and the ICP analysis are not typed
 * in (v1.6.2): the server uses the aquarium's stored data, which ZeovitInputsNotice names.
 */
function ZeovitNewRecommendationPageContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const createRecommendation = useCreateZeovitRecommendation(aquariumId);

  const photoLabelId = useId();
  const photoHintId = useId();
  const photoErrorId = useId();
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState<PhotoError>(null);
  // "Continue anyway" on the update request: not asked again in this flow.
  const [inputsAcknowledged, setInputsAcknowledged] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitError, setSubmitError] = useState<SubmitError>(null);
  const [concerns, setConcerns] = useState<ZeovitConcern[]>([]);
  const [concernNote, setConcernNote] = useState('');
  const uploadedRef = useRef<UploadedPhoto | null>(null);

  // Focus the page title on entry (spec: focus moves to the PageHeader heading).
  useEffect(() => {
    document.getElementById(PAGE_HEADING_ID)?.focus();
  }, []);

  // After a failed generation, move focus to the error notice.
  useEffect(() => {
    if (submitError) alertRef.current?.focus();
  }, [submitError]);

  const detailBase = `/aquariums/${encodeURIComponent(aquariumId)}`;

  const handleCancel = () => {
    navigate(detailBase);
  };

  const handlePhotoChange = (file: File) => {
    setPhoto(file);
    setPhotoError(null);
  };

  const generate = async () => {
    setPhotoError(photo ? null : 'required');
    setSubmitError(null);

    if (!photo) {
      dropzoneRef.current?.focus();
      return;
    }

    setGenerating(true);
    try {
      let referenceImageKey = uploadedRef.current?.file === photo ? uploadedRef.current.key : null;
      if (referenceImageKey === null) {
        const [image] = await uploadGalleryImages(aquariumId, [photo]);
        if (!image?.s3Key) throw new Error('Upload returned no object key');
        referenceImageKey = image.s3Key;
        uploadedRef.current = { file: photo, key: referenceImageKey };
      }
      // The server adds the latest stored measurements and ICP analysis.
      const note = concernNote.trim();
      const result = await createRecommendation.mutateAsync({
        referenceImageKey,
        ...(concerns.length > 0 ? { concerns } : {}),
        ...(note ? { concernNote: note } : {}),
      });
      // The page unmounts on navigation, so "ready" is announced by the app's polite toast region.
      showToast(t('zeovit.new.generated'), 'success');
      navigate(
        `${detailBase}/zeovit-recommendations/${encodeURIComponent(result.recommendation.id)}`,
      );
    } catch (error) {
      setGenerating(false);
      if (getAiLimitErrorKind(error) !== null) {
        setSubmitError({ kind: 'aiLimit', error });
      } else if (error instanceof ApiError && error.status === 409 && error.code === SUPPLY_SYSTEM_NOT_ZEOVIT) {
        setSubmitError({ kind: 'notZeovit' });
      } else if (error instanceof ApiError && error.status === 409 && error.code === AQUARIUM_VOLUME_REQUIRED_CODE) {
        setSubmitError({ kind: 'volume' });
      } else if (error instanceof ApiError && error.code === 'IMAGE_UNAVAILABLE') {
        // The photo could not be used — ask for it again.
        uploadedRef.current = null;
        setPhotoError('unavailable');
        dropzoneRef.current?.focus();
      } else {
        setSubmitError({ kind: 'generic' });
      }
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void generate();
  };

  const photoDescribedBy = photoError ? `${photoHintId} ${photoErrorId}` : photoHintId;

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="narrow">
          <PageHeader
            title={t('zeovit.new.title')}
            description={t('zeovit.new.description')}
            headingProps={{ id: PAGE_HEADING_ID, tabIndex: -1 }}
            actions={
              generating ? undefined : (
                <Button variant="secondary" onClick={handleCancel}>
                  {t('zeovit.new.cancel')}
                </Button>
              )
            }
          />

          {generating ? (
            <Card>
              <Stack gap="3" align="center">
                <LiveRegion busy>
                  <Text color="muted">{t('zeovit.new.generating')}</Text>
                </LiveRegion>
              </Stack>
            </Card>
          ) : (
            <Stack gap="8">
              {submitError?.kind === 'generic' ? (
                <Alert
                  ref={alertRef}
                  tabIndex={-1}
                  tone="danger"
                  action={
                    <Button variant="secondary" onClick={() => void generate()}>
                      {t('zeovit.new.retry')}
                    </Button>
                  }
                >
                  {t('zeovit.new.error.generic')}
                </Alert>
              ) : null}
              {submitError?.kind === 'aiLimit' ? (
                <AiLimitErrorMessage ref={alertRef} tabIndex={-1} error={submitError.error} />
              ) : null}
              {submitError?.kind === 'notZeovit' ? (
                <ZeovitUnavailableNotice ref={alertRef} tabIndex={-1} aquariumId={aquariumId} />
              ) : null}
              {submitError?.kind === 'volume' ? (
                <ZeovitVolumeRequiredNotice ref={alertRef} tabIndex={-1} aquariumId={aquariumId} />
              ) : null}

              <form onSubmit={handleSubmit} noValidate aria-labelledby={PAGE_HEADING_ID}>
                <Stack gap="8">
                  <Section title={t('zeovit.new.photoSection')}>
                    <Card>
                      <Stack gap="4">
                        <Stack gap="2">
                          <Text id={photoLabelId} weight="medium">
                            {t('zeovit.new.photoLabel')}
                          </Text>
                          <PhotoUploadDropzone
                            ref={dropzoneRef}
                            ariaLabel={t('zeovit.new.photoLabel')}
                            ariaDescribedBy={photoDescribedBy}
                            maxSizeMB={MAX_PHOTO_MB}
                            onChange={handlePhotoChange}
                          />
                        </Stack>
                        <Text id={photoHintId} color="muted" size="sm">
                          {t('zeovit.new.photoHint')}
                        </Text>
                        {photoError ? (
                          <FieldError id={photoErrorId}>
                            {photoError === 'required'
                              ? t('zeovit.new.error.photoRequired')
                              : t('zeovit.new.error.imageUnavailable')}
                          </FieldError>
                        ) : null}
                      </Stack>
                    </Card>
                  </Section>

                  <ZeovitConcernsFieldset
                    concerns={concerns}
                    note={concernNote}
                    onConcernsChange={setConcerns}
                    onNoteChange={setConcernNote}
                  />

                  <ZeovitInputsNotice
                    aquariumId={aquariumId}
                    acknowledged={inputsAcknowledged}
                    onContinue={() => setInputsAcknowledged(true)}
                  />

                  <Inline gap="2" wrap className="zeovit-new-recommendation__actions">
                    <Button type="submit" variant="primary">
                      {t('zeovit.new.submit')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={handleCancel}>
                      {t('zeovit.new.cancel')}
                    </Button>
                  </Inline>
                </Stack>
              </form>
            </Stack>
          )}
        </PageContent>
      }
    />
  );
}

/**
 * Only for aquariums whose supply system is ZeoVit (#1784): otherwise the page shows
 * the info notice instead (ZeovitModuleGate).
 */
export function ZeovitNewRecommendationPage() {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  return (
    <ZeovitModuleGate aquariumId={aquariumId} title={t('zeovit.new.title')} width="narrow">
      <ZeovitNewRecommendationPageContent />
    </ZeovitModuleGate>
  );
}

export default ZeovitNewRecommendationPage;
